import { useEffect } from "react";

const DataDeletion = () => {
  useEffect(() => {
    const title = "Data Deletion Instructions | YieldSocial";
    const description = "How to request deletion of your YieldSocial account and personal data.";

    document.title = title;

    const metaDesc = document.querySelector('meta[name="description"]') || document.createElement('meta');
    metaDesc.setAttribute('name', 'description');
    metaDesc.setAttribute('content', description);
    if (!metaDesc.parentNode) document.head.appendChild(metaDesc);

    const canonical = document.querySelector('link[rel="canonical"]') || document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    canonical.setAttribute('href', window.location.href);
    if (!canonical.parentNode) document.head.appendChild(canonical);

    const ld = document.createElement('script');
    ld.type = 'application/ld+json';
    ld.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: title,
      description,
      step: [
        { '@type': 'HowToStep', name: 'Send deletion request', text: 'Email us from your account email with subject: Account Deletion Request.' },
        { '@type': 'HowToStep', name: 'Verify ownership', text: 'We may request verification to confirm you own the account.' },
        { '@type': 'HowToStep', name: 'Deletion processing', text: 'We will delete your account and associated personal data within 30 days, unless retention is required by law.' }
      ]
    });
    document.head.appendChild(ld);
    return () => {
      document.head.removeChild(ld);
    };
  }, []);

  return (
    <main className="min-h-screen bg-gradient-to-br from-green-50 to-amber-50">
      <section className="mx-auto max-w-3xl p-6 md:p-10">
        <h1 className="text-3xl font-semibold tracking-tight text-gray-900 mb-4">User Data Deletion</h1>
        <p className="text-sm text-gray-600 mb-8">Last updated: {new Date().toLocaleDateString()}</p>

        <div className="space-y-6 text-gray-800 leading-relaxed">
          <p>
            You can request deletion of your YieldSocial account and associated personal data at any time.
            Follow the instructions below.
          </p>

          <section>
            <h2 className="text-xl font-semibold mb-2">How to Request Deletion</h2>
            <ol className="list-decimal pl-5 space-y-2">
              <li>
                Email us at <a className="underline" href="mailto:nancy@truesty.com?subject=Account%20Deletion%20Request">nancy@truesty.com</a> from the email address linked to your account.
              </li>
              <li>
                Include: your full name (optional), the email used to sign up, and a clear request to delete your account and data.
              </li>
              <li>
                We may ask for additional verification to confirm ownership for your security.
              </li>
            </ol>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">What Will Be Deleted</h2>
            <p>
              Upon verification, we delete your authentication account and associated personal data stored in
              our databases. Some data may be retained in backups for up to 30 days and will be purged during
              normal backup rotation. We may retain data where required by law (e.g., fraud prevention).
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">Timeframe</h2>
            <p>
              We aim to complete deletion within 30 days of verifying your request. You will receive a
              confirmation email once deletion is complete.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">Data Export (Optional)</h2>
            <p>
              If you would like a copy of your data before deletion, please request an export in the same
              email. We will provide available data in a portable format.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">Contact</h2>
            <p>
              For any questions about data deletion, contact <a className="underline" href="mailto:nancy@truesty.com">nancy@truesty.com</a>.
            </p>
          </section>
        </div>
      </section>
    </main>
  );
};

export default DataDeletion;
